/**
 * Typed FPA endpoints (docs/CONTRACT.md → HTTP API).
 */
import { request } from './client'
import {
  normalizeAccountSettings,
  normalizeBudgetOptions,
  normalizeDepartmentSettings,
  normalizePeriods,
  normalizeRunway,
} from './normalize'
import type {
  AccessRole,
  AccessUser,
  AccountClassification,
  AccountSetting,
  AddUserInput,
  BudgetOption,
  BudgetPin,
  DepartmentSettings,
  DirectorySearch,
  Me,
  Periods,
  Runway,
  SyncRun,
  TileInput,
  TileMode,
  Txn,
} from './types'

/**
 * Which period (and budget) to read (fpa-backend ADR-0006): `year` + `months` (any months of one
 * year), or a `quarter` (`YYYY-Qn`); `asOf` (`YYYY-MM-DD`) is the as-of date inside it (alone: the
 * legacy quarter containing it). Nothing = the current quarter as of today (server side).
 */
export interface PeriodSelection {
  year?: number
  /** Calendar months 1-12, ascending; requires `year`. */
  months?: number[]
  quarter?: string
  asOf?: string
  budgetId?: string
}

/** The query parameters of a period selection (budget excluded). */
export function periodParams(sel: PeriodSelection): Record<string, string | undefined> {
  if (sel.year !== undefined) {
    return {
      year: String(sel.year),
      months: sel.months && sel.months.length > 0 ? sel.months.join(',') : undefined,
      asOf: sel.asOf,
    }
  }
  return { quarter: sel.quarter, asOf: sel.asOf }
}

export function getMe(signal?: AbortSignal): Promise<Me> {
  return request<Me>('/v1/me', { signal })
}

export async function getPeriods(signal?: AbortSignal): Promise<Periods> {
  return normalizePeriods(await request<Periods>('/v1/periods', { signal }))
}

export async function getRunway(sel: PeriodSelection = {}, signal?: AbortSignal): Promise<Runway> {
  const raw = await request<Runway>('/v1/runway', {
    params: { ...periodParams(sel), budgetId: sel.budgetId },
    signal,
  })
  return normalizeRunway(raw)
}

export function getDepartmentTransactions(
  code: string,
  sel: PeriodSelection = {},
  signal?: AbortSignal,
): Promise<Txn[]> {
  return request<Txn[]>(`/v1/runway/departments/${encodeURIComponent(code)}/transactions`, {
    params: periodParams(sel),
    signal,
  })
}

export async function getBudgets(sel: PeriodSelection, signal?: AbortSignal): Promise<BudgetOption[]> {
  return normalizeBudgetOptions(await request<BudgetOption[]>('/v1/budgets', { params: periodParams(sel), signal }))
}

/** Which tiles the dashboard shows (admin, manager). */
export function setTileMode(mode: TileMode): Promise<{ mode: TileMode }> {
  return request<{ mode: TileMode }>('/v1/settings/tile-mode', { method: 'PUT', body: { mode } })
}

/** Pin FPA's budget (admin); `null` follows Sage's default again. */
export function pinBudget(budgetId: string | null): Promise<BudgetPin> {
  return request<BudgetPin>('/v1/settings/budget', { method: 'PUT', body: { budgetId } })
}

export async function getAccountSettings(quarter: string | undefined, signal?: AbortSignal): Promise<AccountSetting[]> {
  return normalizeAccountSettings(
    await request<AccountSetting[]>('/v1/settings/accounts', { params: { quarter }, signal }),
  )
}

export async function classifyAccounts(
  accounts: string[],
  classification: AccountClassification,
): Promise<AccountSetting[]> {
  return normalizeAccountSettings(
    await request<AccountSetting[]>('/v1/settings/accounts', { method: 'PUT', body: { accounts, classification } }),
  )
}

export async function getDepartmentSettings(signal?: AbortSignal): Promise<DepartmentSettings> {
  return normalizeDepartmentSettings(await request<DepartmentSettings>('/v1/settings/departments', { signal }))
}

export async function saveDepartmentSettings(tiles: TileInput[]): Promise<DepartmentSettings> {
  return normalizeDepartmentSettings(
    await request<DepartmentSettings>('/v1/settings/departments', { method: 'PUT', body: { tiles } }),
  )
}

export function getSyncRuns(signal?: AbortSignal): Promise<SyncRun[]> {
  return request<SyncRun[]>('/v1/sync/runs', { signal })
}

export function triggerSync(): Promise<SyncRun> {
  return request<SyncRun>('/v1/sync', { method: 'POST' })
}

export function getAccessUsers(signal?: AbortSignal): Promise<AccessUser[]> {
  return request<AccessUser[]>('/v1/access', { signal })
}

/** identity's person directory; `q` under 2 characters only probes availability. */
export function searchDirectory(q: string, signal?: AbortSignal): Promise<DirectorySearch> {
  return request<DirectorySearch>('/v1/access/directory', { params: { q }, signal })
}

export function addAccessUser(input: AddUserInput): Promise<AccessUser> {
  return request<AccessUser>('/v1/access', { method: 'POST', body: input })
}

export function changeAccessRole(id: string, role: AccessRole): Promise<AccessUser> {
  return request<AccessUser>(`/v1/access/${encodeURIComponent(id)}`, { method: 'PATCH', body: { role } })
}

export function removeAccessUser(id: string): Promise<void> {
  return request<void>(`/v1/access/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export const periodKey = (sel: PeriodSelection): string =>
  sel.year !== undefined
    ? `${sel.year}:${(sel.months ?? []).join(',') || 'all'}:${sel.asOf ?? 'latest'}`
    : (sel.quarter ?? (sel.asOf ? `asOf:${sel.asOf}` : 'current'))

export const queryKeys = {
  me: ['me'] as const,
  periods: ['periods'] as const,
  /** Prefix of every runway query (invalidate after a pin or a settings change). */
  runwayAll: ['runway'] as const,
  runway: (sel: PeriodSelection) => ['runway', periodKey(sel), sel.budgetId ?? 'resolved'] as const,
  transactionsAll: ['transactions'] as const,
  transactions: (code: string, sel: PeriodSelection) => ['transactions', code, periodKey(sel)] as const,
  budgetsAll: ['budgets'] as const,
  budgets: (sel: PeriodSelection) => ['budgets', periodKey(sel)] as const,
  accountSettingsAll: ['settings', 'accounts'] as const,
  accountSettings: (quarter: string | undefined) => ['settings', 'accounts', quarter ?? 'current'] as const,
  departmentSettings: ['settings', 'departments'] as const,
  syncRuns: ['sync', 'runs'] as const,
  access: ['access', 'users'] as const,
  directory: (q: string) => ['access', 'directory', q] as const,
}
